import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@chakra-ui/react';
import dayjs from 'dayjs';
import _get from 'lodash/get';

import i18n from 'app/i18n';
import { DataTable } from 'app/common/components/data-table';
import { useFetchReviewerStatementsQuery } from 'app/apis/reviews';
import { ReviewerStatement } from 'app/types/reviews';

import styles from 'app/queue/views/statements/statements.module.scss';

const getStatementsTableData = (statements: ReviewerStatement[]) => {
  return statements.map((statement) => {
    const { id, archive_url, start_date, end_date } = statement;

    return {
      [i18n.t('table.columns.startDate')]: {
        data: dayjs.utc(start_date).format('DD MMMM YYYY'),
        statement_id: id,
      },
      [i18n.t('table.columns.endDate')]: {
        data: dayjs.utc(end_date).subtract(1, 'day').format('DD MMMM YYYY'),
        statement_id: id,
      },
      [i18n.t('table.columns.archive')]: {
        data: (
          <a href={archive_url} download>
            {i18n.t('common.download')}
          </a>
        ),
        statement_id: id,
      },
    };
  });
};

export const sortComparator = (sortColumn: string, row: Record<string, unknown>) => {
  if (sortColumn === i18n.t('table.columns.archive')) {
    return _get(row, [sortColumn, 'id']);
  } else {
    return dayjs(_get(row, [sortColumn, 'data']));
  }
};

export const Statements = () => {
  const { t } = useTranslation();
  const start_date = useMemo(() => dayjs().startOf('month').subtract(2, 'years').toISOString(), []);
  const {
    data: statements = [],
    isLoading,
    isUninitialized,
  } = useFetchReviewerStatementsQuery({
    start_date,
  });

  const isAllLoading = isLoading || isUninitialized;
  const statementsTableData = getStatementsTableData(statements);

  return (
    <div>
      <div className={styles['title-bar']}>
        <h1>{t('queue.monthlyStatements')}</h1>
      </div>

      {isAllLoading ? (
        <div className={styles['loader']}>
          <div>
            <Spinner />
          </div>
        </div>
      ) : (
        <div className={styles.content}>
          <DataTable
            rows={statementsTableData}
            data-tst="statements-table"
            className={styles['statements-table']}
            sortComparator={sortComparator}
          />
        </div>
      )}
    </div>
  );
};

export default Statements;
